'use client';
import { Typography } from '@curology/ui-components-web/react/Typography';
import Image from 'next/image';
import React, { useEffect } from 'react';

import RegistrationForm from '@/components/registrationForm';
import SurveyResponseCarousel from '@/components/surveyResponseCarousel';
import { type TreatmentCategory } from '@/consts/treatmentCategories';
import FLOW_TYPES, {
  type FlowType,
} from '@/services/funnelService/consts/flowType';
import { type AccountCreationStep } from '@/services/funnelService/models/accountCreationStep';
import { type SurveyDataResource } from '@/services/funnelService/types';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';

import ProviderCard from './components/providerCard';
import { BackButton } from '../../components/ProgressButton/BackButton';
import { SocialSignupButtons } from '../../components/SocialSignupButtons/SocialSignupButtons';

const ClientContainer = ({
  step,
  flowType,
  treatmentCategory,
  hairSurveyResponses,
  skinSurveyResponses,
  preShipmentNotifications,
  nextBtnTreatmentText,
}: {
  step: AccountCreationStep;
  flowType: FlowType;
  treatmentCategory: TreatmentCategory;
  hairSurveyResponses?: SurveyDataResource[];
  skinSurveyResponses?: SurveyDataResource[];
  preShipmentNotifications: boolean;
  nextBtnTreatmentText?: string;
}) => {
  const nextButtonText = nextBtnTreatmentText || step.nextBtnText;

  useEffect(() => {
    trackEvent(AnalyticsEvent.FLAG_EVALUATED, {
      treatmentCategory: treatmentCategory,
      flag: 'curology-skin-pre-shipment-notifications',
      variation: preShipmentNotifications,
    });
  }, [treatmentCategory, preShipmentNotifications]);

  return (
    <section className="cur-px-4 cur-py-8 cur-my-0">
      {preShipmentNotifications &&
      treatmentCategory === 'skin' &&
      flowType !== FLOW_TYPES.OneTimeRxSkin ? (
        <div className="cur-w-[384px] cur-max-w-full cur-mx-auto">
          <Image
            alt="Curology Products"
            src="https://images.ctfassets.net/mdcr7mahi0vp/6WACQMKsln3lY0kelTm7CH/868c586685bc542655774dd30f463d34/pre_shipment_notifications_products_v2.jpg?h=250"
            quality={100}
            width={384}
            height={200}
            className="cur-inline-block cur-max-w-none cur-pb-2 cur-w-full"
          />
          <Typography size="h2" className="cur-text-primary cur-pb-2">
            Try your essential routine for 30 days—just $14.95
          </Typography>
          <Typography size="body" className="cur-pb-3">
            We’ll send a reminder 2 days before your next box ships.
          </Typography>
          <Image
            alt="Curology Products"
            src="https://images.ctfassets.net/mdcr7mahi0vp/7ja6WtXSpsHRG2ksA0L3kF/9624bd8d573cf2d04e47cc0ec9fa6f62/pre_shipment_notifications_schedule_v2.png?h=250"
            quality={100}
            width={384}
            height={150}
            className="cur-inline-block cur-max-w-none cur-w-full"
          />
        </div>
      ) : (
        <div>
          <div className="cur-w-[384px] cur-max-w-full cur-mx-auto">
            <Typography className="cur-leading-10 cur-text-primary" size="h2">
              {skinSurveyResponses ? (
                'Now create an account to see your recommendation'
              ) : (
                <span dangerouslySetInnerHTML={{ __html: step.title }} />
              )}
            </Typography>
            <Typography className="cur-text-primary cur-leading-6 cur-pt-4">
              {!!skinSurveyResponses && (
                <>
                  Thanks for sharing! We’ll save your progress and show your
                  recommendation next.
                </>
              )}
              {step.subtitle && (
                <span dangerouslySetInnerHTML={{ __html: step.subtitle }} />
              )}
            </Typography>
          </div>
          {skinSurveyResponses && (
            <SurveyResponseCarousel surveyResponses={skinSurveyResponses} />
          )}
          {hairSurveyResponses &&
            step.accountCreationVariant === 'before_recommendations' && (
              <SurveyResponseCarousel surveyResponses={hairSurveyResponses} />
            )}
          {treatmentCategory === 'hair' &&
            step.accountCreationVariant === 'before_medical' && (
              <ProviderCard />
            )}
          {!skinSurveyResponses && step.image && (
            <>
              <div className="cur-mt-8 cur-overflow-x-auto cur-text-center">
                <Image
                  priority
                  src={step.image.src}
                  alt={step.image.alt}
                  quality={100}
                  width={step.image.width}
                  height={step.image.height}
                  className="cur-inline-block cur-max-w-none"
                />
              </div>
              {step.imageCaption && (
                <div
                  style={{ width: `${step.image.width}px`, margin: 'auto' }}
                  className="cur-text-primary cur-pt-2"
                >
                  <Typography className="cur-text-primary" size="h3">
                    <span
                      dangerouslySetInnerHTML={{ __html: step.imageCaption }}
                    />
                  </Typography>
                </div>
              )}
            </>
          )}
        </div>
      )}
      <div className="cur-w-[384px] cur-max-w-full cur-mx-auto">
        <div className="cur-mt-5">
          <RegistrationForm
            brandTransferUrl={step.brandTransferUrl}
            nextButtonText={nextButtonText}
          />
        </div>
        {step.backBtnText && <BackButton>{step.backBtnText}</BackButton>}
        <div className="cur-flex cur-flex-col cur-gap-6 cur-pt-10">
          <SocialSignupButtons />
        </div>
        <div
          className="cur-text-center cur-font-body cur-text-primary cur-mt-8 cur-leading-6"
          dangerouslySetInnerHTML={{
            // @ts-expect-error - TODO: broken by strictNullChecks
            __html: step.footnote,
          }}
        />
        <Typography
          className="cur-text-primary cur-py-6 cur-text-center"
          size="meta"
        >
          {/* @ts-expect-error - TODO: broken by strictNullChecks */}
          <span dangerouslySetInnerHTML={{ __html: step.citation }} />
        </Typography>
      </div>
    </section>
  );
};

export default ClientContainer;
